import { Badge } from "@hightouch/htui";
import { useFlags } from "launchdarkly-react-client-sdk";

import { Icon } from "src/components/layout/nav/icons/agents";
import { NavItem } from "src/components/layout/nav/nav-item";
import { NavSection } from "src/components/layout/nav/nav-section";
import { ActiveSectionProps } from "src/components/layout/nav/utils";
import { useResourcePermission } from "src/components/permission/use-resource-permission";
import { useSpaceContext } from "src/components/spaces";
import { useAiFeatureSettings } from "src/hooks/use-ai-feature-settings";
import { useAgentsEnabledFlag } from "src/pages/agents/hooks/use-agents-enabled-flag";
import { useShouldShowAgentsTos } from "src/pages/agents/hooks/use-should-show-agents-tos";

import { UseSectionHook } from "./types";

export const useAgentsSection: UseSectionHook = () => {
  const isEnabled = useAgentsEnabledFlag();
  const { shouldShowAgentsTos } = useShouldShowAgentsTos();
  const { agenticInitiatives } = useFlags();
  const { isPermitted: canConfigureAgents } = useResourcePermission({
    v2: { resource: "workspace", grant: "can_configure_agents" },
  });
  const rightElement =
    isEnabled && shouldShowAgentsTos ? (
      <Badge size="sm" variant="info">
        Try now
      </Badge>
    ) : undefined;

  // For users who are not flagged into agents or legacy copilot, we'll just show a splash page on /agents.
  if (!isEnabled) {
    return {
      title: "Agents",
      rightElement,
      isEnabled: true,
      links: [
        {
          href: "/agents",
          label: "Agents",
        },
      ],
    };
  }

  return {
    title: "Agents",
    rightElement,
    isEnabled: true,
    links: [
      {
        isSub: true,
        href: "/agents/chats",
        label: "Chats",
      },
      {
        isSub: true,
        href: "/agents/reports",
        label: "Reports",
      },
      {
        isSub: true,
        href: "/agents/custom-agents",
        label: "Custom agents",
      },
      ...(agenticInitiatives
        ? [
            {
              isSub: true as const,
              href: "/agents/initiatives",
              label: "Initiatives",
            },
          ]
        : []),
      ...(canConfigureAgents
        ? [
            {
              isSub: true as const,
              href: "/agents/settings",
              label: "Settings",
            },
          ]
        : []),
    ],
  };
};

export const Agents = (props: ActiveSectionProps) => {
  const section = useAgentsSection();
  const isEnabled = useAgentsEnabledFlag();
  const { agents: agentsFeatureEnabled } = useAiFeatureSettings();
  const { isInSpace, isPathAccessibleInSpace } = useSpaceContext();
  const { isPermitted: canViewAgents } = useResourcePermission({
    v2: { resource: "workspace", grant: "can_view_agents" },
  });

  // Hide when in a space and the path is not accessible
  if (isInSpace && !isPathAccessibleInSpace("/agents")) {
    return null;
  }

  // Hide entire section if user lacks permission to view agents
  if (!canViewAgents) {
    return null;
  }

  // Hide entirely when the org has turned the Agents feature off.
  if (!agentsFeatureEnabled) {
    return null;
  }

  // Just show a single nav item for the agents section if the user is not flagged into agents or legacy copilot.
  if (!isEnabled) {
    return (
      <NavItem
        href="/agents"
        icon={Icon}
        label="Agents"
        rightElement={section.rightElement}
      />
    );
  }

  return <NavSection icon={Icon} {...section} {...props} />;
};
